import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Card, Divider, Grid, Typography } from "@mui/material";
import moment from "moment/moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  addDirectBilling,
  getAllClinicPaymentOfHugeRecord,
  getBillingDashboardData,
  getClinicUsers,
  getCollectionSummaryWoLoader,
  getRecentTransactionsPayment,
  registerClinicUser,
  updateMultiplePayment,
} from "../../../redux/reducers/clinicSlice";
import { deletePaymentById } from "../../../redux/reducers/paymentSlice";
import {
  getClinicPetList,
  getPetByUserId,
} from "../../../redux/reducers/petSlice";
import { AppColors } from "../../../util/AppColors";
import { w10h10GrayIcon } from "../../../util/CommonIconStyle";
import {
  billAsForBillingHistory,
  paymentSourceTypes,
} from "../../../util/dropList";
import { paymentStatusEnum } from "../../../util/enum";
import {
  getPaymentStatus,
  getPetShopRecordBasedType,
  getPharmacyRecordBasedType,
  getTaxAndSubTotalAndTotal,
  getTreatmentRecordBasedType,
  roundWithSignedDiff,
} from "../../../util/function";
import { canWriteModule } from "../../../util/roleModuleAccess";
import CreateClinicPet from "../../CommonComponents/CreateClinicPet";
import PaymentModeDropdown from "../../CommonComponents/PaymentModeDropdown";
import PaymentReturn from "../../CommonScreens/Payments/PaymentReturn";
import CreateBillEstimateModal from "./CreateBillEstimateModal";

const initValues = { customer: null, searchValue: "", invoiceNumber: [] };
const formatPetDob = (dob) => {
  if (!dob) return "";
  const parsed = moment(dob);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "";
};

const petBreedLabel = (breed) => {
  if (breed == null) return "";
  if (typeof breed === "object") return breed?.value ?? breed?.name ?? "";
  return String(breed);
};

const toPetList = (payload) =>
  Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

const initSaleValues = {
  billAs: "Pharmacy",
  searchType: "user",
  visible: false,
  searchEnabled: false,
  user: null,
  pet: null,
  petSearch: "",
  searchValue: "",
  backDated: false,
  billDate: null,
};

const collectionSummarySourceList = Array.isArray(paymentSourceTypes)
  ? paymentSourceTypes.filter((li) => li?.value)
  : [];

const collectionSummaryAllOption = collectionSummarySourceList.find(
  (li) => li?.value?.toLowerCase?.() === "all",
);

const collectionSummaryAllSources = collectionSummarySourceList.filter(
  (li) => li?.value?.toLowerCase?.() !== "all",
);

const isCollectionSummaryAllOption = (li) =>
  li?.value?.toLowerCase?.() === "all";

const normalizeCollectionSummarySources = (nextValue = [], prevValue = []) => {
  const list = Array.isArray(nextValue) ? nextValue.filter(Boolean) : [];
  if (list.length === 0) return [];

  const prevList = Array.isArray(prevValue) ? prevValue : [];
  const prevHadAll = prevList.some(isCollectionSummaryAllOption);
  const nextHasAll = list.some(isCollectionSummaryAllOption);
  const nonAll = list.filter((li) => !isCollectionSummaryAllOption(li));

  // Selecting All clears individual sources
  if (nextHasAll && !prevHadAll) {
    return collectionSummaryAllOption ? [collectionSummaryAllOption] : nonAll;
  }

  // Selecting an individual while All is selected removes All
  if (prevHadAll && nextHasAll && nonAll.length > 0) {
    return nonAll;
  }

  // If every individual source is selected, collapse to All
  if (
    !nextHasAll &&
    collectionSummaryAllSources.length > 0 &&
    nonAll.length === collectionSummaryAllSources.length &&
    collectionSummaryAllSources.every((src) =>
      nonAll.some((li) => li?.value === src?.value),
    )
  ) {
    return collectionSummaryAllOption ? [collectionSummaryAllOption] : nonAll;
  }

  if (nextHasAll) {
    return collectionSummaryAllOption ? [collectionSummaryAllOption] : [];
  }

  return nonAll;
};

const getCollectionSummarySourceValues = (sources) => {
  if (!Array.isArray(sources) || sources.length === 0) return [];
  const hasAll = sources.some(isCollectionSummaryAllOption);
  const list = hasAll ? collectionSummaryAllSources : sources;
  return list
    .map((li) => li?.reportValue ?? li?.value?.toLowerCase?.() ?? "")
    .filter((val) => val && val !== "all");
};

const PaymentDashboard = ({
  setActiveTab,
  setPosModVsble,
  setSelecetdPayment,
  setPaymentId,
  setPaymentTableType,
  setSelectedPaymentTab,
}) => {
  const dispatch = useDispatch();
  const canManageBilling = canWriteModule("billing");
  const [delModVsble, setDelModVsble] = useState(false);
  const [collectPayModVsble, setCollectPayModVsble] = useState(false);
  const [newSaleValues, setNewSaleValues] = useState(initSaleValues);
  const [modValues, setModValues] = useState(initValues);
  const [modErrors, setModErrors] = useState({});
  const [posSelectedPayment, setPosSelectedPayment] = useState(null);
  const [retModVsble, setRetModVsble] = useState(false);
  const [petsByUserId, setPetsByUserId] = useState([]);
  const [saleSearchUsers, setSaleSearchUsers] = useState([]);
  const [saleSearchPets, setSaleSearchPets] = useState([]);
  const searchReqIdRef = useRef(0);
  const [chartDurationType, setCharDurationType] = useState("today");
  const [chartSources, setChartSources] = useState(
    collectionSummaryAllOption
      ? [collectionSummaryAllOption]
      : collectionSummaryAllSources,
  );
  const [createUserModVsble, setCreateUserModVsble] = useState(false);
  const [createPetModVsble, setCreatePetModVsble] = useState(false);
  const [petCreateUser, setPetCreateUser] = useState(null);
  const [estimateModOpen, setEstimateModOpen] = useState(false);
  const [usrValues, setUsrValues] = useState({});
  const [usrErrs, setUsrErrs] = useState({});
  const {
    billDashboardData,
    users,
    hugePayments,
    details: clinicData,
    dashboardCollectionSummary,
  } = useSelector((state) => state?.clinic);
  const dashData = billDashboardData?.data ?? {};
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { dataSharing: branchesDataSharing, clinic: clinicDetails } =
    useMemo(() => {
      if (!mandateDataRaw) {
        return { dataSharing: {}, clinic: {} };
      }
      try {
        const reqData = JSON.parse(mandateDataRaw);
        return {
          dataSharing: reqData?.dataSharing ?? {},
          clinic: reqData?.clinic ?? {},
        };
      } catch {
        return { dataSharing: {}, clinic: {} };
      }
    }, [mandateDataRaw]);
  const clinicDet = clinicDetails;

  useEffect(() => {
    onApiCall();
  }, []);

  useEffect(() => {
    if (!clinicDet?.id) return;
    try {
      const today = moment();
      if (!today.isValid()) return;

      let url = `?clinicId=${clinicDet.id}`;
      if (chartDurationType === "today") {
        const todayDate = today.format("YYYY-MM-DD");
        url += `&startDate=${todayDate}&endDate=${todayDate}`;
      } else if (chartDurationType === "week") {
        url += `&startDate=${today.clone().startOf("week").format("YYYY-MM-DD")}&endDate=${today.format("YYYY-MM-DD")}`;
      } else if (chartDurationType === "month") {
        url += `&startDate=${today.clone().startOf("month").format("YYYY-MM-DD")}&endDate=${today.format("YYYY-MM-DD")}`;
      } else if (chartDurationType === "year") {
        url += `&startDate=${today.clone().startOf("year").format("YYYY-MM-DD")}&endDate=${today.format("YYYY-MM-DD")}`;
      }

      const sourceValues = getCollectionSummarySourceValues(chartSources);
      if (sourceValues.length > 0) {
        url += `&source=${encodeURIComponent(sourceValues.join(","))}`;
      }

      dispatch(getCollectionSummaryWoLoader(url));
    } catch {
      // ignore bad date/source input
    }
  }, [chartDurationType, chartSources, clinicDet?.id, dispatch]);

  useEffect(() => {
    if (modValues?.searchValue?.length > 2) {
      dispatch(
        getClinicUsers(`?search=${modValues?.searchValue}&itemSize=50&page=1`),
      );
    }
  }, [modValues?.searchValue]);

  useEffect(() => {
    setSaleSearchUsers([]);
    setSaleSearchPets([]);

    const value = newSaleValues?.searchValue?.trim();
    if (!value || !newSaleValues?.searchEnabled) {
      searchReqIdRef.current += 1;
      return;
    }

    const timeoutId = setTimeout(() => {
      const reqId = ++searchReqIdRef.current;
      if (newSaleValues?.searchType === "user") {
        dispatch(
          getClinicUsers(
            `?search=${value}&itemSize=30&page=1&userData=${branchesDataSharing?.petData ?? false}`,
          ),
        ).then((res) => {
          if (reqId !== searchReqIdRef.current) return;
          setSaleSearchUsers(res?.payload ?? []);
        });
      } else {
        dispatch(
          getClinicPetList({
            type: "all",
            page: 1,
            itemSize: 1000,
            petName: value,
            petData: branchesDataSharing?.petData,
          }),
        ).then((res) => {
          if (reqId !== searchReqIdRef.current) return;
          setSaleSearchPets(res?.payload?.data ?? []);
        });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    newSaleValues?.searchValue,
    newSaleValues?.searchType,
    newSaleValues?.searchEnabled,
    branchesDataSharing?.petData,
    dispatch,
  ]);

  const onApiCall = () => {
    dispatch(getBillingDashboardData());
  };

  const parseCollectionAmount = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (value == null || value === "") return 0;

    const normalized = String(value)
      .replace(/Rs\.?/gi, "")
      .replace(/,/g, "")
      .trim();

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const collectionSummaryPayload = useMemo(() => {
    const summary =
      dashboardCollectionSummary?.data ?? dashboardCollectionSummary ?? {};
    return summary?.data ?? summary;
  }, [dashboardCollectionSummary]);

  const collectionChartRows = useMemo(() => {
    return (collectionSummaryPayload?.paymentModeRows ?? [])
      .map((row) => {
        const amount = parseCollectionAmount(
          row?.amount ?? row?.amountValue ?? row?.value,
        );
        return {
          label: row?.paymentMode ?? "",
          amount,
        };
      })
      .filter((row) => row.label && row.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [collectionSummaryPayload]);

  const collectionChartColors = [
    "#0047AB",
    "#E1D9FF",
    "#B829F4",
    "#DCE3F2",
    "#00C49F",
  ];

  const collectionSummaryTooltipFormatter = useCallback(
    ({ dataPointIndex, w }) => {
      const item = collectionChartRows?.[dataPointIndex];
      if (!item) return "";

      const color =
        w?.globals?.colors?.[dataPointIndex] ??
        collectionChartColors[dataPointIndex % collectionChartColors.length] ??
        "#0047AB";
      const amount = Number(item?.amount ?? 0).toLocaleString("en-IN");

      return (
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
        item.label +
        '<span class="apexcharts-tooltip-marker" style="background-color: ' +
        color +
        '; margin-left: 5px; width: 12px; height: 12px; border-radius: 2px; display: inline-block;"></span>' +
        "</div>" +
        '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
        '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
        '<div class="apexcharts-tooltip-y-group">' +
        '<span class="apexcharts-tooltip-text-y-label">Amount: </span>' +
        '<span class="apexcharts-tooltip-text-y-value" style="font-weight: 600;">' +
        `Rs. ${amount}` +
        "</span>" +
        "</div>" +
        "</div>" +
        "</div>"
      );
    },
    [collectionChartRows],
  );

  const collectionSummaryChartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "40%",
          borderRadius: 4,
          distributed: true,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: collectionChartRows.map((item) => item.label),
        labels: {
          style: { fontSize: "12px", colors: "#2C2C49" },
          rotate: -45,
          trim: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => val.toLocaleString("en-IN"),
        },
      },
      grid: { strokeDashArray: 4 },
      colors: collectionChartColors,
      legend: { show: false },
      tooltip: {
        custom: collectionSummaryTooltipFormatter,
      },
    }),
    [collectionChartRows, collectionSummaryTooltipFormatter],
  );

  const collectionSummaryChartSeries = useMemo(
    () => [
      {
        name: "Amount",
        data: collectionChartRows.map((item) => Number(item.amount) || 0),
      },
    ],
    [collectionChartRows],
  );

  const collectionToggleGroupSx = {
    backgroundColor: "#f0f2f5",
    borderRadius: "6px",
    padding: "1px",
    height: "26px",
    display: "inline-flex",
    alignItems: "center",
    "& .MuiToggleButtonGroup-grouped": {
      borderRadius: "4px",
      border: "none",
      borderColor: "transparent",
      color: "#333",
      transition: "none",
      padding: "0 6px",
      height: "24px",
      minHeight: "24px",
      maxHeight: "24px",
      fontSize: "10px",
      fontWeight: 500,
      lineHeight: 1.1,
      textTransform: "none",
      margin: 0,
      "&:not(:first-of-type)": {
        marginLeft: "1px",
        borderLeft: "none",
      },
      "&.Mui-selected": {
        backgroundColor: "#005EB8",
        color: "#fff",
      },
      "&.Mui-selected:hover": {
        backgroundColor: "#005EB8",
      },
    },
  };

  const onViewAllTrans = (typ = null) => {
    setSelectedPaymentTab(
      typ === "open"
        ? "Pending"
        : typ === "unpaid" || typ === "all"
          ? "Finalized"
          : "Collections",
    );
    setPaymentTableType(typ === "open" ? null : typ);
    setActiveTab("Payments");
  };

  const onNewSale = () => {
    setNewSaleValues({ ...newSaleValues, visible: true });
    // setPaymentId(null);
    // setPosModVsble(true);
    // setActiveTab("PointOfSales");
  };

  const onCloseNewSaleMod = () => {
    setNewSaleValues(initSaleValues);
  };

  const onEditPos = (itm) => {
    setPaymentId(itm?.paymentId ?? null);
    setSelecetdPayment(itm ?? null);
    setPosModVsble(true);
    setActiveTab("PointOfSales");
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setSelecetdPayment(null);
  };

  const onConfirmDelete = () => {
    if (!posSelectedPayment?.paymentId) return;
    dispatch(deletePaymentById(posSelectedPayment?.paymentId)).then((res) => {
      if (res?.payload) {
        onCloseDelMod();
        onApiCall();
        dispatch(getRecentTransactionsPayment("?filter=all&type=resent"));
      }
    });
  };

  const onDeletePos = (itm) => {
    setPosSelectedPayment(itm);
    setDelModVsble(true);
  };

  const onReturn = () => {
    setRetModVsble(true);
  };

  const onCloseReturn = (typ = null) => {
    if (typ === "apiSuccess") {
      setActiveTab("Returns");
    }
    setRetModVsble(false);
  };

  const getPaidPercentage = () => {
    let percentageChange = 0;
    const yesTotal = dashData?.yesterdayPaidTotalAmount ?? 0;
    const todayTotal = dashData?.paidTotalAmount ?? 0;
    if (Number(yesTotal) === 0) {
      percentageChange = Number(todayTotal ?? 0) === 0 ? 0 : 100;
    } else {
      percentageChange =
        Number(
          Number(Number(todayTotal ?? 0) - Number(yesTotal ?? 0)) /
            Number(yesTotal ?? 0),
        ) * 100;
    }
    return Math.floor(percentageChange ?? 0);
  };

  const onCloseCollectPay = () => {
    setCollectPayModVsble(false);
    setModValues(initValues);
  };

  const onCollectPay = () => {
    setCollectPayModVsble(true);
  };

  const onChangeModValue = (name, value) => {
    const reqValue = typeof value === "object" ? value?.value : value;
    const reqObj = { ...modValues, [name]: value };
    const errs = {
      ...modErrors,
      [name]:
        (typeof reqValue === "number" ? reqValue?.toString() : reqValue)
          ?.length > 0
          ? ""
          : "This field is required",
    };
    if (name === "invoiceNumber") {
      const reqList =
        modValues?.invoiceNumber?.some((li) => li?.value === "All") &&
        !value?.some((li) => li?.value === "All")
          ? []
          : value?.some((li) => li?.value === "All")
            ? getInvoiceList()
            : value;
      reqObj.invoiceNumber = reqList;
      reqObj.amount = Number(
        reqList?.reduce((acc, obj) => {
          return acc + (Number(obj?.total ?? 0) ?? 0);
        }, 0),
      );
      errs.invoiceNumber =
        (value ?? [])?.length > 0 ? "" : "This field is required";
    }
    if (name === "amount") {
      if (modValues?.paymentMode?.value === "Advance Adjustment") {
        errs.amount =
          Number(value) < 0
            ? "Amount must be positive"
            : Number(value) > Number(getInvoiceList()?.[0]?.advanceAmount ?? 0)
              ? "Insufficient credit balance"
              : "";
      } else {
        errs.amount = Number(value) < 0 ? "Amount must be positive" : "";
      }
      if (
        Number(value) >
        modValues?.invoiceNumber?.reduce((acc, obj) => {
          return acc + (Number(obj?.total ?? 0) ?? 0);
        }, 0)
      ) {
        errs.amount = "Amount must be less than or equal to balance due";
      }
    }
    setModErrors(errs);
    setModValues(reqObj);
  };

  const getFilteredUserList = () => {
    const search = String(modValues?.searchValue ?? "").toLowerCase();
    const includesText = (value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(search);
    const list = Array.isArray(users) ? users : [];
    return list.filter(
      (usr) =>
        includesText(usr?.name) ||
        includesText(usr?.email) ||
        includesText(usr?.mobile),
    );
  };

  const handleSelectedUser = (user) => {
    const modObj = {
      ...modValues,
      customer: { name: user?.name, value: user?.id },
      searchValue: "",
    };
    setModErrors({ ...modErrors, customer: "" });
    dispatch(
      getAllClinicPaymentOfHugeRecord(
        `?filter=all&type=all&page=1&itemSize=30&userId=${user?.id}`,
      ),
    );
    setModValues(modObj);
  };

  const getInvoiceList = () => {
    const reqList = (hugePayments?.data ?? [])?.map((li) => {
      const total = roundWithSignedDiff(
        Number(
          getTaxAndSubTotalAndTotal(
            getTreatmentRecordBasedType(li?.paymentDetails),
          )?.total +
            getTaxAndSubTotalAndTotal(
              getPharmacyRecordBasedType(li?.paymentDetails),
            )?.total +
            getTaxAndSubTotalAndTotal(
              getPetShopRecordBasedType(li?.paymentDetails),
            )?.total,
        ) - Number(li?.amtPaid ?? 0),
      )?.rounded;
      const invoiceNumbers = [
        li?.invoiceNumbers ?? "",
        li?.petShopInvoiceNumber ?? "",
        li?.treatmentInvoiceNumber ?? "",
      ]?.filter(Boolean);
      const reqObj = {
        name: `${invoiceNumbers?.[0]}, ₹${total}, ${moment(new Date(li?.createdDate)).format("DD MMM YYYY")}`,
        value: (li?.paymentId ?? "")?.toString(),
        paymentId: li?.paymentId,
        total,
        advanceAmount: li?.advanceAmount,
      };
      return reqObj;
    });
    const listWithAmount = [
      {
        name: "All",
        value: "All",
        total: 0,
        advanceAmount: hugePayments?.data?.[0]?.advanceAmount ?? 0,
      },
      ...(reqList ?? [])?.filter((li) => li?.total > 0),
    ];
    return listWithAmount;
  };

  const onClickPay = () => {
    let errs = {};
    if (
      !modValues?.amount ||
      isNaN(Number(modValues?.amount)) ||
      Number(modValues?.amount) <= 0 ||
      Number(modValues?.amount) >
        modValues?.invoiceNumber?.reduce((acc, obj) => {
          return acc + (Number(obj?.total ?? 0) ?? 0);
        }, 0) ||
      (modValues?.paymentMode?.value === "Advance Adjustment" &&
        Number(modValues?.amount) >
          Number(getInvoiceList()?.[0]?.advanceAmount ?? 0))
    ) {
      errs = {
        ...errs,
        amount:
          modValues?.paymentMode?.value === "Advance Adjustment" &&
          Number(modValues?.amount) >
            Number(getInvoiceList()?.[0]?.advanceAmount ?? 0)
            ? "Insufficient credit balance"
            : !modValues?.amount
              ? "This field is required"
              : Number(modValues?.amount) >
                  modValues?.invoiceNumber?.reduce((acc, obj) => {
                    return acc + (Number(obj?.total ?? 0) ?? 0);
                  }, 0)
                ? "Amount must be less than or equal to balance due"
                : Number(modValues?.amount) <= 0
                  ? "Amount must be positive"
                  : "",
      };
    }
    if (!modValues?.paymentMode?.value) {
      errs = { ...errs, paymentMode: "This field is required" };
    }
    if (modValues?.invoiceNumber?.length === 0) {
      errs = { ...errs, invoiceNumber: "This field is required" };
    }
    if (Object.values(errs).some((item) => item?.length > 0)) {
      return setModErrors(errs);
    }
    if (
      (modValues?.invoiceNumber ?? [])?.filter((li) => li?.value !== "All")
        ?.length === 0
    ) {
      return;
    }
    const multiplePayments = modValues?.invoiceNumber
      ?.filter((bil) => bil?.value !== "All")
      ?.map((bil) => ({ paymentId: bil?.paymentId }))
      ?.sort((a, b) => a?.paymentId - b?.paymentId);
    dispatch(
      updateMultiplePayment({
        multiplePayments,
        amount: Number(modValues?.amount),
        mode: modValues?.paymentMode?.value,
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseCollectPay();
        onApiCall();
      }
    });
  };

  const userListByFilter = () => {
    if (newSaleValues?.searchValue === "") return [];
    const search = String(newSaleValues?.searchValue ?? "").toLowerCase();
    const includesText = (value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(search);
    const reqList = (
      Array.isArray(saleSearchUsers) ? saleSearchUsers : []
    ).filter(
      (usr) =>
        includesText(usr?.name) ||
        includesText(usr?.email) ||
        includesText(usr?.mobile),
    );
    return reqList;
  };

  const onNewSaleChange = (name, value) => {
    const reqObj = { ...newSaleValues, [name]: value };
    if (name === "backDated" && value) {
      reqObj.billDate = new Date();
    }
    if (name === "searchType") {
      reqObj.searchValue = "";
      reqObj.pet = null;
      reqObj.user = null;
      setSaleSearchUsers([]);
      setSaleSearchPets([]);
    }
    if (name === "user" || name === "pet") {
      reqObj.error = false;
      reqObj.searchEnabled = false;
      if (name === "user") {
        if (value?.id) {
          dispatch(getPetByUserId(value?.id)).then((res) => {
            setPetsByUserId(
              toPetList(res?.payload).map((pt) => ({
                ...pt,
                name: pt?.petName,
                value: pt?.petId,
                userName: reqObj?.user?.name,
              })),
            );
          });
        }
        reqObj.pet = null;
      }
      if (name === "pet") reqObj.user = null;
    }
    if (name === "userPet") {
      reqObj.petSearch = "";
      reqObj.userPetErr = false;
    }
    if (name === "searchValue") {
      setSaleSearchUsers([]);
      setSaleSearchPets([]);
      reqObj.searchEnabled = true;
    }
    setNewSaleValues(reqObj);
  };

  const onCreateNewSale = () => {
    if (
      !newSaleValues?.user?.id &&
      // || !newSaleValues?.userPet?.petId
      !newSaleValues?.pet?.petId
    ) {
      return setNewSaleValues({
        ...newSaleValues,
        error: !newSaleValues?.user?.id && !newSaleValues?.pet?.petId,
        // userPetErr:
        //   newSaleValues?.searchType === "user" &&
        //   !newSaleValues?.userPet?.petId,
      });
    }
    const reqData = {
      type: newSaleValues?.billAs,
      paymentOption: "draft",
      direct: [],
      discountValue: 0,
      discountType: "percentage",
      referenceNo: "",
      estimate: false,
      checkOut: "N",
      userId:
        newSaleValues?.searchType === "user"
          ? newSaleValues?.user?.id
          : newSaleValues?.pet?.userId,
      petId:
        newSaleValues?.searchType === "user"
          ? (newSaleValues?.userPet?.petId ?? null)
          : newSaleValues?.pet?.petId,
    };
    // if (newSaleValues?.user?.id) reqData.userId = newSaleValues?.user?.id;
    // if (newSaleValues?.pet?.petId) {
    //   reqData.petId = newSaleValues?.pet?.petId;
    //   reqData.userId = newSaleValues?.pet?.userId;
    // }
    if (newSaleValues?.backDated) {
      reqData.backDated = moment(new Date(newSaleValues?.billDate)).format(
        "YYYY-MM-DD",
      );
    }
    dispatch(addDirectBilling(reqData)).then((res) => {
      if (res?.payload) {
        setNewSaleValues(initSaleValues);
        setSelecetdPayment({
          paymentId: res?.payload?.data,
          type: newSaleValues?.billAs,
        });
        setActiveTab("PointOfSales");
      }
    });
  };

  const reqDraftPayments = () => {
    const reqList = (dashData?.draftPayments ?? [])?.filter(
      (li) =>
        (li?.type === "Pharmacy" || li?.type === "Pet Shop") &&
        (li?.checkOut === "Y" ? false : true) &&
        !li?.invoiceNumbers,
      // li?.paymentOption === "draft" &&
      // (li?.type === "Pharmacy" || li?.type === "Pet Shop") &&
      // Number(li?.balanceDue) > 0 &&
      // li?.status === "unpaid",
    );

    return reqList;
  };

  const handleAddUser = () => {
    setNewSaleValues({ ...newSaleValues, searchValue: "" });
    setCreateUserModVsble(true);
  };

  const handleAddPet = (user) => {
    setPetCreateUser(user?.id || user?.userId ? user : null);
    setCreatePetModVsble(true);
  };

  const onCloseCreatePet = (status, petData) => {
    const sourceUser = petCreateUser;
    setCreatePetModVsble(false);
    setPetCreateUser(null);
    if (status !== "petCreated" || !petData || typeof petData !== "object")
      return;
    const petId = petData?.petId ?? petData?.id;
    if (petId == null) return;
    const userId =
      petData?.userId ?? sourceUser?.id ?? sourceUser?.userId ?? null;
    const breedName = petBreedLabel(petData?.breed);
    const pet = {
      petId,
      petName: petData?.petName ?? petData?.name ?? "",
      userId,
      userName: "",
      breed: breedName,
      dob: petData?.dob ?? null,
    };
    setNewSaleValues((prev) => {
      const userName =
        petData?.userName ??
        sourceUser?.name ??
        sourceUser?.userName ??
        prev?.user?.name ??
        "";
      const nextPet = { ...pet, userName };
      if (prev?.searchType === "pet") {
        return {
          ...prev,
          searchValue: "",
          petSearch: "",
          searchEnabled: false,
          error: false,
          userPetErr: false,
          user: null,
          pet: nextPet,
          userPet: null,
        };
      }
      const user = userId
        ? {
            ...(prev?.user?.id === userId ? prev.user : {}),
            id: userId,
            name: userName,
            mobile:
              petData?.mobile ??
              petData?.userMobile ??
              sourceUser?.mobile ??
              sourceUser?.userMobile ??
              prev?.user?.mobile,
            email:
              petData?.email ??
              petData?.userEmail ??
              sourceUser?.email ??
              sourceUser?.userEmail ??
              prev?.user?.email,
          }
        : prev?.user;
      return {
        ...prev,
        searchValue: "",
        petSearch: "",
        searchEnabled: false,
        error: false,
        userPetErr: false,
        user,
        pet: null,
        userPet: nextPet,
      };
    });
    if (userId) {
      dispatch(getPetByUserId(userId)).then((res) => {
        setPetsByUserId(
          toPetList(res?.payload).map((pt) => ({
            ...pt,
            name: pt?.petName,
            value: pt?.petId,
            userName:
              petData?.userName ??
              sourceUser?.name ??
              sourceUser?.userName ??
              pt?.userName,
          })),
        );
      });
    }
  };

  const petModalUser = petCreateUser
    ? {
        id: petCreateUser?.id ?? petCreateUser?.userId,
        userId: petCreateUser?.id ?? petCreateUser?.userId,
        name: petCreateUser?.name ?? petCreateUser?.userName,
        userName: petCreateUser?.name ?? petCreateUser?.userName,
        email: petCreateUser?.email ?? petCreateUser?.userEmail,
        userEmail: petCreateUser?.email ?? petCreateUser?.userEmail,
        mobile: petCreateUser?.mobile ?? petCreateUser?.userMobile,
        userMobile: petCreateUser?.mobile ?? petCreateUser?.userMobile,
      }
    : null;

  const handleValueChange = (e, name) => {
    const value = e?.target?.value;
    setUsrValues({ ...usrValues, [name]: value });
    setUsrErrs({
      ...usrErrs,
      [name]:
        value?.length > 0
          ? ""
          : name === "email"
            ? ""
            : "This field is required",
    });
  };

  const onCloseUserMod = () => {
    setCreateUserModVsble(false);
    setUsrValues({});
    setUsrErrs({});
  };

  const onCreateUser = () => {
    if (
      usrValues?.userName?.length === 0 ||
      !usrValues?.userName ||
      usrValues?.userMobile?.length === 0 ||
      !usrValues?.userMobile
    ) {
      let errs = { ...usrErrs };
      if (usrValues?.userName?.length === 0 || !usrValues?.userName) {
        errs = { ...errs, userName: "This field is required" };
      }
      if (usrValues?.userMobile?.length === 0 || !usrValues?.userMobile) {
        errs = { ...errs, userMobile: "This field is required" };
      }
      setUsrErrs(errs);
      return;
    }
    const form = new FormData();
    form.append("userName", usrValues?.userName);
    form.append("userMobile", usrValues?.userMobile);
    if (usrValues?.email?.length > 0) form.append("email", usrValues?.email);
    dispatch(registerClinicUser(form)).then((res) => {
      if (res?.payload) {
        const reqVal = res?.payload?.data ?? {};
        const usrObj = {
          id: reqVal?.id,
          mobile: reqVal?.mobile ?? usrValues?.userMobile,
          name: reqVal?.name ?? usrValues?.userName,
          email: reqVal?.email ?? usrValues?.email,
          userImage: null,
          advanceAmount: 0,
          outstandingAmount: 0,
        };
        setNewSaleValues({
          ...newSaleValues,
          searchValue: "",
          searchEnabled: false,
          user: usrObj,
        });
        onCloseUserMod();
      }
    });
  };

  return (
    <>
      <div className="ph20 scroll-90vh">
        <div className="flex-row-between mv10">
          <div className="w30Per">
            <div className="bil-dash-card bil-card-green">
              <div className="p15">
                <Typography className="fs12 font-bold gray-color">
                  Today's Collection
                </Typography>
                <Typography className="fs30 font-bold black">
                  ₹
                  {dashData?.paidTotalAmount?.toString()?.length > 0
                    ? Number(dashData?.paidTotalAmount)?.toFixed(0)
                    : dashData?.paidTotalAmount}
                </Typography>
                <div className="flex-row-ali-cen">
                  {getPaidPercentage() > 0 ? (
                    <ArrowUpwardIcon sx={w10h10GrayIcon} />
                  ) : (
                    <ArrowDownwardIcon sx={w10h10GrayIcon} />
                  )}
                  <Typography className="fs12 font-medium gray-color ml2">
                    {getPaidPercentage()}% vs yesterday · Today's Refund{" ₹"}
                    {dashData?.refund?.toString()?.length > 0
                      ? Number(dashData?.refund)?.toFixed(0)
                      : dashData?.refund}
                  </Typography>
                </div>
                <div className="mv5">
                  <Divider />
                </div>
                <Typography
                  className="blue-color fs12 txt-semi-bold underline cursor mt10"
                  onClick={() => onViewAllTrans("today")}
                >
                  View today's transactions
                </Typography>
              </div>
            </div>
          </div>
          <div className="w30Per">
            <div className="bil-dash-card bil-card-orange">
              <div className="p15">
                <Typography className="fs12 font-bold gray-color">
                  Outstanding Balance
                </Typography>
                <Typography className="fs30 font-bold black">
                  ₹
                  {dashData?.unpaidTotalAmount?.toString()?.length > 0
                    ? Number(dashData?.unpaidTotalAmount)?.toFixed(0)
                    : dashData?.unpaidTotalAmount}
                </Typography>
                <Typography className="fs12 font-medium gray-color">
                  across {dashData?.outstandingBillCount ?? 0} invoices
                </Typography>
                <div className="mv5">
                  <Divider />
                </div>
                <Typography
                  className="blue-color fs12 txt-semi-bold underline cursor mt10"
                  onClick={() => onViewAllTrans("unpaid")}
                >
                  View all unpaid
                </Typography>
              </div>
            </div>
          </div>
          <div className="w30Per">
            <div className="bil-dash-card bil-card-blue">
              <div className="p15">
                <Typography className="fs12 font-bold gray-color">
                  Draft/Open Bills
                  {/* Open Bills */}
                  {/* Pending Drafts */}
                </Typography>
                <Typography className="fs30 font-bold black">
                  {dashData?.totalOpenPaymentCount}
                </Typography>
                <Typography className="fs12 font-medium gray-color">
                  Incomplete transactions
                </Typography>
                <div className="mv5">
                  <Divider />
                </div>
                {/* <Typography className="fs12 font-medium gray-color">
                  Completed transactions {dashData?.totalCompletedPaymentCount}
                </Typography> */}
                <Typography
                  className="blue-color fs12 txt-semi-bold underline cursor mt10"
                  onClick={() => onViewAllTrans("open")}
                >
                  View all open bills
                </Typography>
                {/* <Typography className="blue-color fs12 txt-semi-bold underline cursor mt10">
                  Complete drafts
                </Typography> */}
              </div>
            </div>
          </div>
        </div>

        {canManageBilling ? (
          <div className="mv20 flex-row-ali-cen">
            <div>
              <CustomButton text="Create Bill" onClick={onNewSale} smallBtn />
            </div>
            <div className="ml15">
              <CustomButton
                text="Create Estimate"
                onClick={() => setEstimateModOpen(true)}
                smallBtn
              />
            </div>
            {/* <div className="ml15">
            <CustomButton
              text="Collect Payment"
              onClick={onCollectPay}
              smallBtn
            />
          </div> */}
            <div className="ml15">
              <CustomButton
                text="Finalize Bill"
                onClick={() => setActiveTab("Payments")}
                smallBtn
              />
            </div>
            {/* <div className="ml15">
            <CustomButton text="Process Return" onClick={onReturn} smallBtn />
          </div> */}
          </div>
        ) : null}

        <div
          className={`flex-row flex-space-btwn mb30 ${canManageBilling ? "" : "mt30"}`}
        >
          <div className="w48Per">
            <div className="bill-card br5 p15">
              <div className="flex-row-between-align-center">
                <Typography className="fs16 blue-color font-bold">
                  Recent Transactions
                </Typography>
                <Typography
                  className="blue-color fs12 txt-semi-bold cursor underline"
                  onClick={() => onViewAllTrans("all")}
                >
                  View all
                </Typography>
              </div>
              <div className="mv10 scroll-37vh">
                {(dashData?.last5transactions ?? [])?.length > 0 ? (
                  (dashData?.last5transactions ?? [])?.map((li, i) => {
                    const invoices = [li?.treatmentInvoiceNumber]?.filter(
                      Boolean,
                    );
                    return (
                      <div key={`${li?.paymentId}_${i}`}>
                        <div className="flex-row">
                          <div className="w65Per">
                            <Typography className="txt-semi-bold fs14 black">{`${li?.petName?.length > 0 ? `${li?.petName} · ` : ""}${li?.userName}`}</Typography>
                            <Typography className="txt-regular fs12 gray28">{`Invoice #${invoices?.join(", ")} · ${moment(new Date(li?.createdDate)).format("DD MMM YYYY, HH:mm")}`}</Typography>
                          </div>
                          <div className="w35Per flex1-end">
                            <div className="flex-row-ali-cen">
                              <Typography className="txt-semi-bold fs14 black">
                                ₹
                                {Number(
                                  roundWithSignedDiff(li?.totalAmt ?? 0)
                                    ?.rounded,
                                )?.toFixed(2)}
                              </Typography>
                              <Typography
                                className={`txt-semi-bold fs10 ml10 ph5 pv2 br5 ${
                                  getPaymentStatus(li) === "paid"
                                    ? "green-back4 green4"
                                    : getPaymentStatus(li)
                                      ? "red4 red-back"
                                      : "card-head-orange-color org-back"
                                }`}
                              >
                                {paymentStatusEnum?.[getPaymentStatus(li)]}
                              </Typography>
                            </div>
                          </div>
                        </div>
                        {(dashData?.last5transactions ?? [])?.length !==
                        i + 1 ? (
                          <div className="mv5">
                            <Divider />
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="no-rec">No records available</div>
                )}
              </div>
            </div>
          </div>
          <div className="w48Per">
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                boxShadow: 2,
                padding: 3,
                minWidth: 400,
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                flexWrap="nowrap"
                sx={{ width: "100%", overflowX: "auto" }}
              >
                <Box sx={{ flexShrink: 0, minWidth: 120 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Collection Summary
                  </Typography>
                  {collectionSummaryPayload?.totalAmount ? (
                    <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
                      {collectionSummaryPayload.totalAmount}
                    </Typography>
                  ) : null}
                </Box>
                <div className="flex-row mt5">
                  <Box sx={{ width: 160, minWidth: 160 }}>
                    <CustomSelect
                      list={collectionSummarySourceList}
                      value={Array.isArray(chartSources) ? chartSources : []}
                      name="chartSources"
                      handleChange={(e) => {
                        const nextValue = Array.isArray(e?.target?.value)
                          ? e.target.value
                          : [];
                        setChartSources((prev) =>
                          normalizeCollectionSummarySources(nextValue, prev),
                        );
                      }}
                      label="Source"
                      multiSelectSearchCheck
                    />
                  </Box>
                  <Box sx={{ width: 160, minWidth: 160 }} className="ml10">
                    <CustomSelect
                      list={[
                        { name: "Today", value: "today" },
                        { name: "This Week", value: "week" },
                        { name: "This Month", value: "month" },
                        { name: "This Year", value: "year" },
                      ]}
                      value={chartDurationType}
                      name="chartDurationType"
                      handleChange={(e) => {
                        setCharDurationType(e?.target?.value);
                      }}
                      label=""
                      select
                    />
                  </Box>
                </div>
              </Box>
              {collectionChartRows.length > 0 ? (
                <Chart
                  options={collectionSummaryChartOptions}
                  series={collectionSummaryChartSeries}
                  type="bar"
                  height={280}
                />
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={280}
                >
                  <Typography className="gray-color fs14">
                    No records available
                  </Typography>
                </Box>
              )}
            </Box>
          </div>
          {/* <div className="w48Per">
            <div className="bill-card br5 p15">
              <div className="flex-row-between-align-center">
                <Typography className="fs16 blue-color font-bold">
                  Draft Transactions
                </Typography>
              </div>
              <div className="mv10 scroll-37vh">
                {reqDraftPayments()?.length > 0 ? (
                  reqDraftPayments()?.map((li, i) => (
                    <div key={`${li?.paymentId}_${i}`}>
                      <div className="flex-row">
                        <div className="w65Per">
                          <Typography className="txt-semi-bold fs14 black">
                            {li?.userName}
                          </Typography>
                          <Typography className="txt-regular fs12 gray28">
                            {moment(new Date(li?.createdDate)).format(
                              "DD MMM YYYY, HH:mm",
                            )}
                          </Typography>
                        </div>
                        <div className="w35Per flex1-end">
                          <div className="flex-row-ali-cen">
                            <Typography className="txt-semi-bold fs14 black">
                              ₹{" "}
                              {Number(
                                getTaxAndSubTotalAndTotal(
                                  li?.type === "Pharmacy"
                                    ? getPharmacyRecordBasedType(
                                        li?.paymentDetails,
                                      )
                                    : getPetShopRecordBasedType(
                                        li?.paymentDetails,
                                      ),
                                )?.subTotal - Number(li?.amtPaid ?? 0),
                              )?.toFixed(2)}
                            </Typography>                            
                            <div
                              className="cursor ml10"
                              onClick={() => onEditPos(li)}
                            >
                              <div className="flex-center">
                                <EditIcon sx={w20h20BlueIcon} />
                              </div>
                              <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                                Edit
                              </Typography>
                            </div>
                            <div
                              className="cursor ml10"
                              onClick={() => onDeletePos(li)}
                            >
                              <div className="flex-center">
                                <DeleteOutlineIcon sx={delBtn} />
                              </div>
                              <Typography className="red3 fs8 txt-semi-bold text-align-center">
                                Delete
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                      {reqDraftPayments()?.length !== i + 1 ? (
                        <div className="mv5">
                          <Divider />
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="no-rec">No records available</div>
                )}
              </div>
            </div>
          </div> */}
        </div>
      </div>
      <PaymentReturn
        returnModVisible={retModVsble}
        onReturnModClose={onCloseReturn}
        headerName="Stock Adjustment"
      />

      <CustomModal
        open={newSaleValues?.visible}
        onClose={onCloseNewSaleMod}
        header="Create Bill"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-85vh ph20">
          {/* {!isBillCombined ? ( */}
          <>
            <div className="font-medium fs14 black2">Bill as</div>
            <CustomSelect
              list={billAsForBillingHistory}
              value={newSaleValues?.billAs}
              handleChange={(e) => onNewSaleChange("billAs", e?.target?.value)}
              select
              // search
            />
          </>
          {/* ) : null} */}
          <div className="font-medium fs14 black2 mt10">Search Type</div>
          <CustomSelect
            list={[
              { name: "Customer", value: "user" },
              { name: "Pet", value: "pet" },
            ]}
            value={newSaleValues?.searchType}
            handleChange={(e) =>
              onNewSaleChange("searchType", e?.target?.value)
            }
            select
            // search
          />
          {newSaleValues?.searchType === "user" ? (
            <div className="flex-end mt5">
              <Typography
                className="blue-color cursor txt-semi-bold fs14 underline"
                onClick={handleAddUser}
              >
                Add new customer
              </Typography>
            </div>
          ) : null}
          {newSaleValues?.searchType === "pet" ? (
            <div className="flex-end mt5">
              <Typography
                className="blue-color cursor txt-semi-bold fs14 underline"
                onClick={() => handleAddPet()}
              >
                Add new pet
              </Typography>
            </div>
          ) : null}
          <div className="mt5" />
          <CustomTextField
            name="searchValue"
            fullWidth
            handleChange={(e) =>
              onNewSaleChange("searchValue", e?.target?.value)
            }
            value={newSaleValues?.searchValue}
            placeholder={`Search ${newSaleValues?.searchType === "user" ? "Customer by Name/Mobile/Email" : "Pet here..."}`}
            error={newSaleValues?.error}
            helperText={
              newSaleValues?.error
                ? `Please Select ${newSaleValues?.searchType === "user" ? "Customer by Name/Mobile/Email" : "Pet here..."}`
                : ""
            }
          />
          {newSaleValues?.searchValue?.length > 0 &&
            newSaleValues?.searchEnabled &&
            newSaleValues?.searchType === "user" && (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {userListByFilter()?.length > 0 &&
                  userListByFilter()?.map((usr) => {
                    return (
                      <div
                        className="flex-row-ali-cen cursor mv10 cus-box"
                        key={usr?.id}
                        onClick={() => onNewSaleChange("user", usr)}
                      >
                        <div className="w15Per">
                          <div className="flex-center">
                            <img
                              src={require("../../../assets/images/png/contact.png")}
                              className="imghw40"
                            />
                          </div>
                        </div>
                        <div className="w75Per">
                          <div className="flex-coloum ml10">
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Parent Name:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {usr?.name}
                              </Typography>
                            </Typography>
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Phone:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {usr?.mobile}
                              </Typography>
                            </Typography>
                            {usr?.secondaryMobile?.length > 0 ? (
                              <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                                Secondary Phone:
                                <Typography className="txt-regular fs12 black15 ml5">
                                  {usr?.secondaryMobile}
                                </Typography>
                              </Typography>
                            ) : null}
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Email:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {usr?.email}
                              </Typography>
                            </Typography>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </Grid>
            )}
          {newSaleValues?.searchValue?.length > 0 &&
            newSaleValues?.searchEnabled &&
            newSaleValues?.searchType === "pet" && (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {(saleSearchPets ?? [])?.length > 0 &&
                  (saleSearchPets ?? [])?.map((petDat) => {
                    const pet = petDat?.listOfDatas;
                    return (
                      <div
                        className="flex-row-ali-cen cursor mv10 cus-box"
                        key={pet?.petId}
                        onClick={() => onNewSaleChange("pet", pet)}
                      >
                        <div className="w15Per">
                          <div className="flex-center">
                            <img
                              src={require("../../../assets/images/png/contact.png")}
                              className="imghw40"
                            />
                          </div>
                        </div>
                        <div className="w75Per">
                          <div className="flex-coloum ml10">
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Pet Name:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {pet?.petName}
                              </Typography>
                            </Typography>
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Parent Name:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {pet?.userName}
                              </Typography>
                            </Typography>
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Breed:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {petBreedLabel(pet?.breed)}
                              </Typography>
                            </Typography>
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              DOB:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {formatPetDob(pet?.dob)}
                              </Typography>
                            </Typography>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </Grid>
            )}

          {(
            !newSaleValues?.searchEnabled &&
            (newSaleValues?.pet?.petId || newSaleValues?.user?.id)
              ? true
              : false
          ) ? (
            <div className="flex-row-ali-cen cursor mv10 cus-box-selected light-blue-back6">
              <div className="w15Per">
                <div className="flex-center">
                  <img
                    src={require("../../../assets/images/png/contact.png")}
                    className="imghw40"
                  />
                </div>
              </div>
              <div className="w75Per">
                <div className="flex-coloum ml10">
                  {newSaleValues?.pet?.petId ? (
                    <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                      Pet Name:
                      <Typography className="txt-regular fs12 black15 ml5">
                        {newSaleValues?.pet?.petName}
                      </Typography>
                    </Typography>
                  ) : null}
                  <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                    Parent Name:
                    <Typography className="txt-regular fs12 black15 ml5">
                      {newSaleValues?.user?.name ??
                        newSaleValues?.pet?.userName}
                    </Typography>
                  </Typography>
                  {newSaleValues?.searchType !== "pet" &&
                  newSaleValues?.user?.id ? (
                    <>
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        Phone:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {newSaleValues?.user?.mobile}
                        </Typography>
                      </Typography>
                      {newSaleValues?.secondaryMobile?.length > 0 ? (
                        <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                          Secondary Phone:
                          <Typography className="txt-regular fs12 black15 ml5">
                            {newSaleValues?.secondaryMobile}
                          </Typography>
                        </Typography>
                      ) : null}
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        Email:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {newSaleValues?.user?.email}
                        </Typography>
                      </Typography>
                    </>
                  ) : null}
                  {newSaleValues?.pet?.petId ? (
                    <>
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        Breed:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {petBreedLabel(newSaleValues?.pet?.breed)}
                        </Typography>
                      </Typography>
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        DOB:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {formatPetDob(newSaleValues?.pet?.dob)}
                        </Typography>
                      </Typography>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          {newSaleValues?.searchType === "user" && newSaleValues?.user?.id ? (
            <>
              <div className="flex-end mt15">
                {newSaleValues?.userPet?.petId ? (
                  <Typography
                    className="red3 cursor txt-semi-bold fs14 underline mr15"
                    onClick={() => onNewSaleChange("userPet", null)}
                  >
                    Remove
                  </Typography>
                ) : null}
                <Typography
                  className="blue-color cursor txt-semi-bold fs14 underline"
                  onClick={() => handleAddPet(newSaleValues?.user)}
                >
                  Add new pet
                </Typography>
              </div>
              {/* <CustomTextField
                name="petSearch"
                fullWidth
                handleChange={(e) =>
                  onNewSaleChange("petSearch", e?.target?.value)
                }
                value={newSaleValues?.petSearch}
                placeholder={`Search Pet here...`}
                error={newSaleValues?.userPetErr}
                helperText={
                  newSaleValues?.userPetErr ? `Please Select Pet` : ""
                }
              /> */}
              {newSaleValues?.userPet?.petId ? (
                <div className="flex-row-ali-cen cursor mv10 cus-box-selected light-blue-back6">
                  <div className="w15Per">
                    <div className="flex-center">
                      <img
                        src={require("../../../assets/images/png/contact.png")}
                        className="imghw40"
                      />
                    </div>
                  </div>
                  <div className="w75Per">
                    <div className="flex-coloum ml10">
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        Pet Name:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {newSaleValues?.userPet?.petName}
                        </Typography>
                      </Typography>

                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        Parent Name:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {newSaleValues?.user?.name ??
                            newSaleValues?.userPet?.userName}
                        </Typography>
                      </Typography>
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        Breed:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {petBreedLabel(newSaleValues?.userPet?.breed)}
                        </Typography>
                      </Typography>
                      <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                        DOB:
                        <Typography className="txt-regular fs12 black15 ml5">
                          {formatPetDob(newSaleValues?.userPet?.dob)}
                        </Typography>
                      </Typography>
                    </div>
                  </div>
                </div>
              ) : (
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  {(petsByUserId ?? [])?.map((pet) => {
                    return (
                      <div
                        className="flex-row-ali-cen cursor mv10 cus-box"
                        key={pet?.petId}
                        onClick={() => onNewSaleChange("userPet", pet)}
                      >
                        <div className="w15Per">
                          <div className="flex-center">
                            <img
                              src={require("../../../assets/images/png/contact.png")}
                              className="imghw40"
                            />
                          </div>
                        </div>
                        <div className="w75Per">
                          <div className="flex-coloum ml10">
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Pet Name:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {pet?.petName}
                              </Typography>
                            </Typography>
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Parent Name:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {pet?.userName}
                              </Typography>
                            </Typography>
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              Breed:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {petBreedLabel(pet?.breed)}
                              </Typography>
                            </Typography>
                            <Typography className="blue-color txt-semi-bold fs12 flex-row-ali-cen">
                              DOB:
                              <Typography className="txt-regular fs12 black15 ml5">
                                {formatPetDob(pet?.dob)}
                              </Typography>
                            </Typography>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Grid>
              )}
            </>
          ) : null}
          <CustomCheckbox
            label="Back Dated"
            onChange={() =>
              onNewSaleChange("backDated", !newSaleValues?.backDated)
            }
            checked={newSaleValues?.backDated}
          />
          {newSaleValues?.backDated ? (
            <CustomTextField
              fullWidth
              mobileDate
              value={newSaleValues?.billDate}
              label="Bill Date"
              handleChange={(e) =>
                onNewSaleChange("billDate", e?.target?.value)
              }
              maxDate={new Date()}
            />
          ) : null}
          <div className="flex1-end mt20">
            <div>
              <CustomButton smallBtn text="Create" onClick={onCreateNewSale} />
            </div>
          </div>
        </div>
      </CustomModal>

      <CreateBillEstimateModal
        open={estimateModOpen}
        onClose={() => setEstimateModOpen(false)}
        mode="estimate"
        onCreated={({ paymentId, type }) => {
          setSelecetdPayment({
            paymentId,
            type,
            estimate: true,
            returnTab: "Dashboard",
          });
          setActiveTab("Estimates");
        }}
        onAddCustomer={() => {
          setEstimateModOpen(false);
          setCreateUserModVsble(true);
        }}
      />

      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure want to delete the payment?
        </Typography>

        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={onCloseDelMod} smallBtn />
          </div>
          <div className="ml10">
            <CustomButton
              redBtn
              text="Confirm"
              onClick={onConfirmDelete}
              smallBtn
            />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={collectPayModVsble}
        onClose={onCloseCollectPay}
        header="Collect Payment"
        modalWidth={30}
        rightModal
      >
        <div className="scroll-80vh">
          <Grid container spacing={2} className="ph20">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="txt-semi-bold fs14 light-grey">
                Type <span className="red-color">*</span>
              </div>
              <CustomSelect
                list={[{ name: "Sales", value: "Sales" }]}
                value={{ name: "Sales", value: "Sales" }}
                search
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <CustomTextField
                fullWidth
                search
                value={modValues?.searchValue}
                placeholder="Search Customer"
                handleChange={(e) =>
                  onChangeModValue("searchValue", e?.target?.value)
                }
                backgroundColor={AppColors.gray6}
              />
            </Grid>
            {modValues?.searchValue?.length > 0 ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {getFilteredUserList()?.length > 0 &&
                  getFilteredUserList()?.map((usr) => {
                    return (
                      <Grid
                        container
                        spacing={1}
                        key={usr?.id}
                        className="cursor"
                        onClick={() => handleSelectedUser(usr)}
                      >
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Card
                                sx={{
                                  borderRadius: 1,
                                  padding: 2,
                                  borderTopColor: AppColors.appPrimary,
                                }}
                                className="CustomCard-back-appointment"
                              >
                                <div className="maint">
                                  <div className="flex-row">
                                    <Grid item>
                                      <div className="flex-row">
                                        <div className="flex-start">
                                          <div className="flex-column">
                                            <div className="flex-row">
                                              <Typography className="mb10 font-bold fs14 capitalize">
                                                {usr?.name}
                                              </Typography>
                                            </div>
                                            <Typography className="mb10 txt-regular card-gray-color fs12">
                                              {usr?.email}
                                            </Typography>
                                            <Typography className="mb10 txt-regular card-gray-color fs12">
                                              {usr?.mobile}
                                            </Typography>
                                          </div>
                                        </div>
                                      </div>
                                    </Grid>
                                  </div>
                                </div>
                              </Card>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  })}
              </Grid>
            ) : null}
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="txt-semi-bold fs14 light-grey">
                Customer
                <span className="red-color">*</span>
              </div>
              <CustomSelect
                list={[]}
                value={modValues?.customer}
                helperText={modErrors?.customer}
                error={!!modErrors?.customer}
                search
                disabled
              />
            </Grid>
            {modValues?.customer?.value ? (
              <>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="txt-semi-bold fs14 light-grey">
                    Invoice Number <span className="red-color">*</span>
                  </div>
                  <CustomSelect
                    list={getInvoiceList()}
                    name="invoiceNumber"
                    handleChange={(e) => {
                      onChangeModValue("invoiceNumber", e?.target?.value);
                    }}
                    value={modValues?.invoiceNumber}
                    helperText={modErrors?.invoiceNumber}
                    error={!!modErrors?.invoiceNumber}
                    multiSelectSearchCheck
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="mh20"
                >
                  <div className="bil-bal-con">
                    <div className="flex-row">
                      <div>
                        <Typography className="font-bold fs14 red-color">
                          Balance Due
                        </Typography>
                      </div>
                      <div className="flex1-end">
                        <Typography className="font-bold fs14 red-color">
                          Rs{" "}
                          {Number(
                            modValues?.invoiceNumber?.reduce((acc, obj) => {
                              return acc + (Number(obj?.total ?? 0) ?? 0);
                            }, 0),
                          )?.toFixed(2)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={6}
                  xl={6}
                  className="mt10"
                >
                  <CustomTextField
                    label="Amount"
                    name="amount"
                    fullWidth
                    handleChange={(e) =>
                      onChangeModValue("amount", e?.target?.value)
                    }
                    value={modValues?.amount}
                    labelTop
                    helperText={modErrors?.amount}
                    error={!!modErrors?.amount}
                    type="number"
                  />
                </Grid>

                <Grid item xs={6} sm={6} md={6} lg={6} xl={6} className="mt10">
                  <div className="font-medium fs14 black2">Payment Mode</div>
                  <PaymentModeDropdown
                    list={[]}
                    value={modValues?.paymentMode}
                    error={!!modErrors?.paymentMode}
                    helperText={modErrors?.paymentMode}
                    onChange={(e, value) => {
                      onChangeModValue("paymentMode", e);
                    }}
                    name="paymentMode"
                    label=""
                  />
                </Grid>
                {modValues?.paymentMode?.value === "Advance Adjustment" ? (
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Typography className="txt-semi-bold fs14 orange5">
                      Available Balance:{" "}
                      {(
                        modValues?.invoiceNumber?.[0]?.advanceAmount ?? ""
                      )?.toString()?.length > 0
                        ? Number(
                            modValues?.invoiceNumber?.[0]?.advanceAmount,
                          )?.toFixed(2)
                        : "-"}
                    </Typography>
                  </Grid>
                ) : null}

                <div className="flex1-end mt20">
                  <div>
                    <CustomButton smallBtn text="Pay" onClick={onClickPay} />
                  </div>
                </div>
              </>
            ) : null}
          </Grid>
        </div>
      </CustomModal>

      {createPetModVsble ? (
        <CreateClinicPet
          createModVisible={createPetModVsble}
          onCloseCreateMod={onCloseCreatePet}
          userDetails={petModalUser}
          selectedStep={petModalUser ? 1 : 0}
          showRegisterOnly
        />
      ) : null}

      <CustomModal
        open={createUserModVsble}
        onClose={onCloseUserMod}
        header="Add new Customer"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh-new ph20 mt10">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt10">
                <CustomTextField
                  label="Parent Name"
                  name="userName"
                  fullWidth
                  handleChange={(e) => handleValueChange(e, "userName")}
                  value={usrValues?.userName}
                  error={!!usrErrs.userName}
                  helperText={usrErrs.userName}
                  required={true}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div>
                <CustomTextField
                  label="Contact Number"
                  name="userMobile"
                  fullWidth
                  handleChange={(e) => handleValueChange(e, "userMobile")}
                  value={usrValues?.userMobile}
                  error={!!usrErrs.userMobile}
                  helperText={usrErrs.userMobile}
                  required={true}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div>
                <CustomTextField
                  label="Email"
                  name="email"
                  fullWidth
                  handleChange={(e) => handleValueChange(e, "email")}
                  value={usrValues?.email}
                  error={!!usrErrs.email}
                  helperText={usrErrs.email}
                  // required={true}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex1-end">
                <div>
                  <CustomButton text="Create" onClick={onCreateUser} smallBtn />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default PaymentDashboard;
